import * as React from 'react';

import featuredStyles from './tabs-featured.module.css';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@wwe-portal/ui/lib/lib';
import { cva } from 'class-variance-authority';

const Tabs = TabsPrimitive.Root;

type Variant = 'default' | 'featured';
interface WithVariants {
  variant?: Variant;
}

const tabListVariants = cva('', {
  variants: {
    variant: {
      default:
        'bg-back200 border-text700 h-[34px] rounded-md border border-solid p-1',
      featured: featuredStyles.tabs,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const tabTriggerVariants = cva('', {
  variants: {
    variant: {
      default:
        'data-[state=active]:bg-text700 text-text400 data-[state=active]:text-green700 h-full rounded-md border-none bg-transparent text-xs font-semibold data-[state=active]:border',
      featured: featuredStyles.trigger,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const tabTriggerLabelVariants = cva('', {
  variants: {
    variant: {
      default: '',
      featured: featuredStyles.triggerLabel,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const tabContentVariants = cva('', {
  variants: {
    variant: {
      default:
        'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      featured: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & WithVariants
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List
    className={cn(tabListVariants({ variant }), className)}
    ref={ref}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & WithVariants
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    className={cn(tabTriggerVariants({ variant }), className)}
    ref={ref}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsTriggerLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & WithVariants
>(({ className, variant, ...props }, ref) => (
  <div
    className={cn(tabTriggerLabelVariants({ variant }), className)}
    ref={ref}
    {...props}
  />
));
TabsTriggerLabel.displayName = 'TabsTriggerLabel';

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & WithVariants
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Content
    className={cn(tabContentVariants({ variant }), className)}
    ref={ref}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsTriggerLabel, TabsContent };
